import React, { useState } from 'react';
import { aiApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { toast } from 'sonner';
import { PenLine, Lightbulb, Sparkles, Wand2, Send, X, CreditCard } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const AIPanel = ({ onClose, context, onInsert, refreshUser, onNavigate }) => {
    const { user } = useAuth();
    const [prompt, setPrompt] = useState('');
    const [assistType, setAssistType] = useState('writing_assist');
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState('');
    const [showLowCredits, setShowLowCredits] = useState(false);

    const assistTypes = [
        { id: 'writing_assist', name: 'Continue Writing', cost: 1, icon: <PenLine className="h-4 w-4" /> },
        { id: 'hook_creation', name: 'Create Hook', cost: 2, icon: <Lightbulb className="h-4 w-4" /> },
        { id: 'arc_creation', name: 'Story Arc', cost: 3, icon: <Sparkles className="h-4 w-4" /> },
        { id: 'story_help', name: 'Story Help', cost: 2, icon: <Wand2 className="h-4 w-4" /> },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setResponse('');
        setShowLowCredits(false);
        try {
            const res = await aiApi.write({
                prompt,
                context: context?.substring(0, 2000) || '',
                assist_type: assistType,
            });
            setResponse(res.data.response);
            refreshUser();
            toast.success(`Used ${res.data.credits_used} credit(s)`);
        } catch (error) {
            if (error.response?.status === 402) {
                toast.error('Insufficient credits. Please purchase more.');
                setShowLowCredits(true);
            } else {
                toast.error('AI request failed');
            }
        } finally {
            setLoading(false);
        }
    };

    const userCredits = user?.credits || 0;
    const isLowCredits = userCredits < 5;

    return (
        <div className="w-96 border-l border-border bg-card flex flex-col">
            <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="font-serif font-semibold">AI Writing Assistant</h3>
                <Button variant="ghost" size="icon" onClick={onClose}>
                    <X className="h-4 w-4" />
                </Button>
            </div>

            {/* Credits Warning Banner */}
            {(isLowCredits || showLowCredits) && (
                <div className="p-3 bg-amber-500/10 border-b border-amber-500/20">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                            <Sparkles className="h-4 w-4" />
                            <span className="text-sm font-medium">
                                {userCredits === 0 ? 'No credits remaining' : `Only ${userCredits} credits left`}
                            </span>
                        </div>
                        <Button 
                            size="sm" 
                            variant="outline"
                            className="text-xs h-7 border-amber-500/30 hover:bg-amber-500/10"
                            onClick={() => onNavigate && onNavigate('credits')}
                            data-testid="ai-panel-buy-credits"
                        >
                            <CreditCard className="h-3 w-3 mr-1" />
                            Buy Credits
                        </Button>
                    </div>
                </div>
            )}

            <div className="p-4 border-b border-border">
                <div className="grid grid-cols-2 gap-2">
                    {assistTypes.map((type) => (
                        <button
                            key={type.id}
                            className={`p-3 rounded-lg text-left transition-colors ${
                                assistType === type.id 
                                    ? 'bg-accent text-accent-foreground' 
                                    : 'bg-muted hover:bg-muted/80'
                            }`}
                            onClick={() => setAssistType(type.id)}
                            data-testid={`ai-type-${type.id}`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                {type.icon}
                                <span className="text-xs font-medium">{type.cost} credit{type.cost > 1 ? 's' : ''}</span>
                            </div>
                            <div className="text-sm font-medium">{type.name}</div>
                        </button>
                    ))}
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-4 border-b border-border">
                <Textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="What do you need help with?"
                    rows={3}
                    className="mb-3"
                    data-testid="ai-prompt-input"
                />
                <Button type="submit" className="w-full" disabled={loading || !prompt.trim()} data-testid="ai-submit-btn">
                    {loading ? 'Generating...' : 'Generate'}
                    <Send className="h-4 w-4 ml-2" />
                </Button>
            </form>

            <ScrollArea className="flex-1 p-4">
                {response && (
                    <div className="space-y-4">
                        <div className="prose prose-sm max-w-none text-foreground">
                            <div className="whitespace-pre-wrap">{response}</div>
                        </div>
                        <Button variant="outline" className="w-full" onClick={() => onInsert(response)} data-testid="ai-insert-btn">
                            Insert into Document
                        </Button>
                    </div>
                )}
            </ScrollArea>
        </div>
    );
};

export default AIPanel;
